// src/components/BankPosting/components/BankPostingTransactionTree.jsx
// ============================================================================
// BLOCK BP.CMP.002 — Transaction tree panel
// ----------------------------------------------------------------------------
// PURPOSE
// - Renders the left-side transaction tree grouped by bank transaction date
// - Shows parent bank transactions with nested assigned invoice child rows
// - Handles local expand/collapse behavior for date groups and transaction rows
// - Supports inline editing of assigned invoice discount overrides
//
// USER EXPERIENCE
// - Parent transaction rows display:
//     [checkbox] [transaction amount] - [remaining balance] - [description]
// - Child assigned invoice rows display:
//     [checkbox] [invoice detail summary] + editable discount field
// - Selected transaction is visually highlighted
// - Checked transaction rows are visually marked for multi-select workflows
//
// EDITING GUIDE
// - Formatting helpers are grouped first
// - Invoice classification / display helpers are grouped second
// - Row components are grouped third
// - Main exported tree component is last
// ============================================================================

import React from 'react';
import PropTypes from 'prop-types';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  buildChildInvoicePrimary,
  buildChildInvoiceSecondary,
} from '../utils/invoiceDisplayFormatters.js';

// ============================================================================
// BLOCK BP.CMP.002.00 — Stable parent transaction key helper
// ----------------------------------------------------------------------------
// PURPOSE
// - Parent transaction rows are keyed by bank_transactions_col.transaction_id
// - Keeps tree selection / checkbox / scoped child keys rebuild-safe
// ============================================================================
function normalizeTransactionKey(value) {
  return String(value ?? '').trim();
}

// ============================================================================
// BLOCK BP.CMP.002.01 — Primitive formatting and numeric safety helpers
// ----------------------------------------------------------------------------
// PURPOSE
// - Keep display formatting and number coercion consistent across the tree
// - Prevent repeated Number(...) / trim / fallback logic in render code
// ============================================================================

/**
 * Formats a numeric value as currency with 2 decimals.
 * Used for all money values shown in transaction and invoice rows.
 *
 * EXAMPLE
 * - 10      => $10.00
 * - "5.5"   => $5.50
 * - null    => $0.00
 */
function money(value) {
  const num = Number(value || 0);
  return `$${num.toFixed(2)}`;
}

/**
 * Converts a value to a number.
 * Returns 0 when the value is not a valid finite number.
 *
 * PURPOSE
 * - Prevent NaN from spreading into balance / total calculations
 */
function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Trims any incoming value into a clean string.
 * Null and undefined safely become an empty string.
 *
 * PURPOSE
 * - Keeps string comparison / display logic predictable
 */
function trimStr(value) {
  return String(value ?? '').trim();
}

const selectableTextFieldSx = {
  '& .MuiInputBase-input': {
    userSelect: 'text',
    WebkitUserSelect: 'text',
    MozUserSelect: 'text',
    cursor: 'text',
  },
  '& .MuiInputBase-root': {
    cursor: 'text',
  },
};

function hasActiveTextSelection() {
  if (typeof window === 'undefined' || typeof window.getSelection !== 'function') {
    return false;
  }

  const selection = window.getSelection();
  return Boolean(selection && String(selection).trim());
}

function cleanBankTransactionDescription(value) {
  return trimStr(value)
    .replace(/preauthorized\s+ach\s+credit\s*/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeDisplayInvoiceNumber(value) {
  const raw = trimStr(value);
  if (!raw) return '';

  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';

  if (digits.length >= 7) {
    return digits.slice(-7);
  }

  return digits.padStart(7, '0');
}

// ============================================================================
// BLOCK BP.CMP.002.02 — Transaction amount / balance calculation helpers
// ----------------------------------------------------------------------------
// PURPOSE
// - Determine how much of a transaction has already been assigned
// - Compute remaining balance shown in parent transaction rows
// ============================================================================

/**
 * Resolves the effective applied amount for a child invoice row.
 *
 * PRIORITY
 * 1. Use explicit applied_amount if present
 * 2. Otherwise use invoice_amount - discount
 * 3. Discount override wins over base invoice_discount when present
 *
 * PURPOSE
 * - Ensures transaction balance reflects the exact currently applied value
 */
function resolveAppliedAmount(invoice) {
  const rawOverride = invoice?.applied_amount_override;

  if (rawOverride !== undefined && rawOverride !== null && String(rawOverride).trim() !== '') {
    const parsedOverride = Number(rawOverride);

    if (Number.isFinite(parsedOverride)) {
      return parsedOverride;
    }
  }

  const rawApplied = invoice?.applied_amount;

  if (rawApplied !== undefined && rawApplied !== null && String(rawApplied).trim() !== '') {
    const parsedApplied = Number(rawApplied);

    if (Number.isFinite(parsedApplied)) {
      return parsedApplied;
    }
  }

  // Auto-fill may seed the payment textbox from the LOKA invoice amount, but a
  // blank payment box must never silently fall back to the invoice total.
  return 0;
}

function resolveSelectedInvoiceTotal(invoice) {
  return resolveAppliedAmount(invoice);
}

function hasExplicitAppliedOverride(invoice) {
  const value = invoice?.applied_amount_override;
  return value !== undefined && value !== null && String(value).trim() !== '';
}

function resolveEffectiveAppliedAmountForBalance(invoice, transactionAmount, otherAppliedTotal) {
  const rawApplied = resolveAppliedAmount(invoice);

  // Business rule: discount is not included in the parent transaction total.
  // It is tracked per invoice for the child-row balance display, but it does not
  // reduce the transaction remaining balance on the parent line.
  if (hasExplicitAppliedOverride(invoice)) {
    return rawApplied;
  }

  if (Number.isFinite(Number(invoice?.applied_amount))) {
    return rawApplied;
  }

  const maxApplicableAmount = Math.max(toNumber(transactionAmount) - otherAppliedTotal, 0);
  return Math.min(rawApplied, maxApplicableAmount);
}

/**
 * Computes the remaining balance for a transaction row.
 *
 * RULES
 * - If no assigned invoices are present:
 *     use row.balance if valid, otherwise fall back to row.amount
 * - If assigned invoices exist:
 *     remaining balance = transaction amount - total applied invoice amounts
 *
 * PURPOSE
 * - Parent row display should always reflect current assignment state
 */
function computeTransactionBalance(row, assignedInvoices) {
  const amount = toNumber(row?.amount);

  if (!Array.isArray(assignedInvoices) || !assignedInvoices.length) {
    const existingBalance = Number(row?.balance);
    return Number.isFinite(existingBalance) ? existingBalance : amount;
  }

  const selectedTotal = assignedInvoices.reduce((sum, invoice) => {
    const otherAppliedTotal = assignedInvoices.reduce(
      (otherSum, candidate) =>
        candidate === invoice ? otherSum : otherSum + resolveSelectedInvoiceTotal(candidate),
      0
    );

    return sum + resolveEffectiveAppliedAmountForBalance(invoice, amount, otherAppliedTotal);
  }, 0);

  return Number((amount - selectedTotal).toFixed(2));
}

function isSubmittableAssignedInvoice(invoice) {
  const invoiceNumber = normalizeDisplayInvoiceNumber(
    invoice?.invoice_number || invoice?.raw_invoice_number || invoice?.masio_invoice_number
  );

  if (!invoiceNumber) {
    return false;
  }

  return resolveAppliedAmount(invoice) > 0.005;
}

function hasSubmittableAssignedInvoices(assignedInvoices) {
  const rows = Array.isArray(assignedInvoices) ? assignedInvoices : [];

  if (!rows.length) {
    return false;
  }

  return rows.every((invoice) => isSubmittableAssignedInvoice(invoice));
}

function hasAssignedInvoicesMissingNumber(assignedInvoices) {
  const rows = Array.isArray(assignedInvoices) ? assignedInvoices : [];

  if (!rows.length) {
    return false;
  }

  return rows.some((invoice) => {
    const invoiceNumber = normalizeDisplayInvoiceNumber(
      invoice?.invoice_number || invoice?.raw_invoice_number || invoice?.masio_invoice_number
    );

    return !invoiceNumber;
  });
}

function isBalancedMissingInvoiceNumberTransaction(row, assignedInvoices) {
  return (
    Math.abs(computeTransactionBalance(row, assignedInvoices)) < 0.005 &&
    hasAssignedInvoicesMissingNumber(assignedInvoices)
  );
}

// Keep these UI thresholds aligned with server shared matching constants.
// Frontend cannot import server modules directly, so these values are mirrored.
const CHILD_ATTACH_MIN_SCORE = 40;
const CHILD_COLOR_GREEN_MIN_SCORE = 80;
const CHILD_COLOR_YELLOW_MIN_SCORE = 40;
const CHILD_COLOR_ORANGE_MIN_SCORE = 10;

/**
 * Returns true when a transaction's effective remaining balance is $0.00.
 *
 * PURPOSE
 * - Drives the green parent-row highlight
 * - Drives the within-date-group display sort so completed rows rise first
 */
function isZeroBalanceTransaction(row, assignedInvoices) {
  return (
    Math.abs(computeTransactionBalance(row, assignedInvoices)) < 0.005 &&
    hasSubmittableAssignedInvoices(assignedInvoices)
  );
}

/**
 * Returns true when the transaction is zero-balanced but was auto-linked with
 * non-strong confidence. These rows show yellow instead of green
 * to signal that the match should be reviewed.
 *
 * DETECTION
 * - link_type 'auto' is stored as match_origin / match_reason = 'auto'
 * - match_tier is classified on the server using shared matching constants
 */
function resolveBalancedTransactionScoreBand(row, assignedInvoices) {
  const rows = Array.isArray(assignedInvoices) ? assignedInvoices : [];
  if (!rows.length) return 'none';

  const isZeroBalance = isZeroBalanceTransaction(row, assignedInvoices);

  // Any non-zero balance transaction with child rows should be review-yellow.
  if (!isZeroBalance) {
    return 'medium';
  }

  const scores = rows
    .map((inv) => resolveComparableScore(inv?.score ?? inv?.match_score ?? inv?.confidence_score))
    .filter((score) => Number.isFinite(score));

  if (scores.length) {
    const topScore = Math.max(...scores);

    if (topScore < CHILD_ATTACH_MIN_SCORE) return 'none';
    if (topScore >= CHILD_COLOR_GREEN_MIN_SCORE) return 'strong';
    if (topScore >= CHILD_COLOR_YELLOW_MIN_SCORE) return 'medium';
    if (topScore >= CHILD_COLOR_ORANGE_MIN_SCORE) return 'low';
    return 'none';
  }

  // Fallback to legacy tier semantics when score data is unavailable.
  const allAuto = rows.every(
    (inv) => trimStr(inv?.match_origin) === 'auto' || trimStr(inv?.match_reason) === 'auto'
  );

  if (!allAuto || !rows.length) return 'medium';

  const hasNonStrongTier = rows.some((inv) => {
    const tier = trimStr(inv?.match_tier).toLowerCase();
    return Boolean(tier) && tier !== 'strong';
  });

  return hasNonStrongTier ? 'medium' : 'strong';
}

function resolveTransactionSortBand(row, assignedInvoices) {
  const isZeroBalance = isZeroBalanceTransaction(row, assignedInvoices);
  const isNeedsInvoiceAction = isBalancedMissingInvoiceNumberTransaction(row, assignedInvoices);
  const scoreBand = resolveBalancedTransactionScoreBand(row, assignedInvoices);

  if (isZeroBalance && scoreBand === 'strong') {
    return 0;
  }

  if (isNeedsInvoiceAction || scoreBand === 'medium') {
    return 1;
  }

  if (scoreBand === 'low') {
    return 2;
  }

  return 3;
}

/**
 * Builds the parent transaction row primary text.
 *
 * DISPLAY FORMAT
 * - [amount] - [remaining balance] - [description]
 *
 * EXAMPLE
 * - $150.00 - $25.00 - PREAUTHORIZED ACH CREDIT ...
 */
function buildTransactionPrimary(row, assignedInvoices) {
  const amount = toNumber(row?.amount);
  const balance = computeTransactionBalance(row, assignedInvoices);
  const description = cleanBankTransactionDescription(
    trimStr(row?.full_description) || trimStr(row?.description)
  );

  return `${money(amount)} - ${money(balance)} - ${description}`;
}

// ============================================================================
// BLOCK BP.CMP.002.03 — Assigned invoice classification helpers
// ----------------------------------------------------------------------------
// PURPOSE
// - Normalize whether a child invoice should be treated as:
//     1. open invoice
//     2. suggested LOKA invoice
// - Centralize source detection so UI formatting stays consistent
// ============================================================================

function resolveComparableScore(value) {
  const parsed = Number(value);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  if (parsed > 0 && parsed < 1) {
    return parsed * 100;
  }

  return parsed;
}

// ============================================================================
// BLOCK BP.CMP.002.04 — Assigned invoice display-text builders
// ----------------------------------------------------------------------------
// PURPOSE
// - Build the text shown in child invoice rows
// - Keep JSX render section compact and easier to scan
// ============================================================================

/**
 * Resolves the current editable discount string for the child TextField.
 *
 * PRIORITY
 * 1. invoice_discount_override when populated
 * 2. invoice_discount
 * 3. default "0.00"
 *
 * PURPOSE
 * - Keeps the input controlled and synced with server-backed values
 */
function resolveEditableDiscount(invoice) {
  if (
    invoice?.invoice_discount_override !== undefined &&
    invoice?.invoice_discount_override !== null &&
    String(invoice.invoice_discount_override).trim() !== ''
  ) {
    return String(toNumber(invoice.invoice_discount_override).toFixed(2));
  }

  if (invoice?.invoice_discount !== undefined && invoice?.invoice_discount !== null) {
    return String(toNumber(invoice.invoice_discount).toFixed(2));
  }

  return '0.00';
}

function resolveEditableAppliedAmount(invoice, maxApplicableAmount = null) {
  if (
    invoice?.applied_amount_override !== undefined &&
    invoice?.applied_amount_override !== null &&
    String(invoice.applied_amount_override).trim() !== ''
  ) {
    return String(toNumber(invoice.applied_amount_override).toFixed(2));
  }

  if (invoice?.applied_amount !== undefined && invoice?.applied_amount !== null) {
    return String(toNumber(invoice.applied_amount).toFixed(2));
  }

  if (invoice?.invoice_amount !== undefined && invoice?.invoice_amount !== null) {
    const baseAmount = toNumber(invoice.invoice_amount);
    if (maxApplicableAmount === null || maxApplicableAmount === undefined) {
      return String(baseAmount.toFixed(2));
    }

    const boundedAmount = Math.min(baseAmount, Math.max(toNumber(maxApplicableAmount), 0));
    return String(boundedAmount.toFixed(2));
  }

  return '0.00';
}

/**
 * Resolves the persisted link id for an assigned invoice row.
 *
 * POSSIBLE SOURCES
 * - invoice.id
 * - invoice.manual_link_id
 *
 * PURPOSE
 * - Needed when saving inline discount edits back to the server
 */
function resolveAssignedLinkId(invoice) {
  const value = invoice?.id ?? invoice?.manual_link_id;

  if (value === undefined || value === null) {
    return '';
  }

  const normalized = String(value).trim();
  return normalized || '';
}

// ============================================================================
// BLOCK BP.CMP.002.05 — Assigned invoice child row component
// ----------------------------------------------------------------------------
// PURPOSE
// - Renders one assigned invoice under a selected/checked transaction
// - Supports child checkbox behavior
// - Supports inline discount editing with save-on-blur
//
// IMPORTANT
// - Discount is only persisted when the normalized value actually changes
// - Invalid numeric input is reverted back to the last resolved value
// ============================================================================
function AssignedInvoiceRow({
  invoice,
  rowId,
  checked,
  onToggle,
  onUpdateAssignedInvoice,
  maxApplicableAmount,
  previewInvoiceMap,
  onPreviewChange,
}) {
  const assignedLinkId = resolveAssignedLinkId(invoice);
  const baseInvoiceKey =
    invoice?.invoice_key || `manual:${assignedLinkId || invoice?.source_invoice_key || 'unlinked'}`;
  const previewInvoiceKey = `${normalizeTransactionKey(rowId)}::${baseInvoiceKey}`;
  const existingPreview = previewInvoiceMap?.[previewInvoiceKey] || null;
  const effectiveInvoice = existingPreview ? { ...invoice, ...existingPreview } : invoice;

  const editableAppliedAmount = resolveEditableAppliedAmount(effectiveInvoice, maxApplicableAmount);
  const editableDiscount = resolveEditableDiscount(effectiveInvoice);
  const inputIdSuffix = `${normalizeTransactionKey(rowId)}-${assignedLinkId || 'unlinked'}`;

  // Local input state allows users to type freely before blur-save runs.
  const [discountValue, setDiscountValue] = React.useState(editableDiscount);
  const [appliedAmountValue, setAppliedAmountValue] = React.useState(editableAppliedAmount);

  // Keep the inputs synced with server-backed values whenever the same link
  // receives refreshed data without changing its link id. This prevents stale
  // zero defaults from remaining visible after an auto-match refresh.
  const isEditingRef = React.useRef(false);
  const idleTimerRef = React.useRef(null);

  React.useEffect(() => {
    // Never overwrite what the user is currently typing.
    if (isEditingRef.current) return;
    setDiscountValue((currentValue) =>
      currentValue === editableDiscount ? currentValue : editableDiscount
    );
    setAppliedAmountValue((currentValue) =>
      currentValue === editableAppliedAmount ? currentValue : editableAppliedAmount
    );
  }, [assignedLinkId, editableDiscount, editableAppliedAmount]);

  const rowPreviewInvoice = React.useMemo(() => {
    const preview = previewInvoiceMap?.[previewInvoiceKey];

    // Never replace the original invoice object.
    // Only overlay Payment/Discount edits.
    return preview
      ? {
          ...invoice,
          ...preview,
        }
      : invoice;
  }, [invoice, previewInvoiceKey, previewInvoiceMap]);

  const latestPersistedRef = React.useRef({
    discount: Number(resolveEditableDiscount(invoice)).toFixed(2),
    appliedAmount: Number(resolveEditableAppliedAmount(invoice, maxApplicableAmount)).toFixed(2),
  });

  React.useEffect(() => {
    latestPersistedRef.current = {
      discount: Number(resolveEditableDiscount(invoice)).toFixed(2),
      appliedAmount: Number(resolveEditableAppliedAmount(invoice, maxApplicableAmount)).toFixed(2),
    };
  }, [assignedLinkId, invoice, maxApplicableAmount]);

  const normalizeMoneyForCompare = React.useCallback((value) => {
    const trimmed = String(value ?? '').trim();

    if (trimmed === '') {
      return '';
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? Number(parsed).toFixed(2) : null;
  }, []);

  const normalizeMoneyForPersist = React.useCallback(
    (value) => {
      const normalized = normalizeMoneyForCompare(value);
      return normalized === '' || normalized === null ? null : Number(normalized);
    },
    [normalizeMoneyForCompare]
  );

  const previewInvoice = React.useMemo(() => {
    const parsedDiscount = normalizeMoneyForCompare(discountValue);
    const parsedAppliedAmount = normalizeMoneyForCompare(appliedAmountValue);

    const nextDiscount =
      parsedDiscount === null || parsedDiscount === ''
        ? Number(invoice?.invoice_discount_override ?? invoice?.invoice_discount ?? 0)
        : Number(parsedDiscount);

    const nextAppliedAmount =
      parsedAppliedAmount === null || parsedAppliedAmount === ''
        ? resolveAppliedAmount(invoice)
        : Number(parsedAppliedAmount);

    // IMPORTANT:
    // Preview state contains ONLY editable values.
    // Invoice metadata remains on the original invoice object.
    return {
      invoice_discount: nextDiscount,
      invoice_discount_override: nextDiscount,
      applied_amount: nextAppliedAmount,
      applied_amount_override: nextAppliedAmount,
    };
  }, [appliedAmountValue, discountValue, invoice, normalizeMoneyForCompare]);

  React.useEffect(() => {
    if (typeof onPreviewChange === 'function') {
      onPreviewChange({
        invoiceKey: previewInvoiceKey,
        previewInvoice,
      });
    }
  }, [onPreviewChange, previewInvoice, previewInvoiceKey]);

  /**
   * Saves the edited discount when the input loses focus.
   *
   * GUARDS
   * - No row id / no update callback => do nothing
   * - No assigned link id => do nothing
   * - Invalid numeric input => revert visible value
   * - No effective change => do nothing
   *
   * PAYLOAD
   * - invoice_discount
   * - is_manual_open_assignment
   */
  const persistEditedValues = React.useCallback(
    async (nextDiscountValue, nextAppliedAmountValue, { revertOnInvalid = true } = {}) => {
      if (!rowId || !onUpdateAssignedInvoice) return;

      if (!assignedLinkId) return;

      const trimmedDiscount = String(nextDiscountValue ?? '').trim();
      const trimmedAppliedAmount = String(nextAppliedAmountValue ?? '').trim();

      const normalizedDiscount = normalizeMoneyForCompare(trimmedDiscount);
      const normalizedAppliedAmount = normalizeMoneyForCompare(trimmedAppliedAmount);

      if (normalizedDiscount === null || normalizedAppliedAmount === null) {
        if (revertOnInvalid) {
          setDiscountValue(editableDiscount);
          setAppliedAmountValue(editableAppliedAmount);
        }
        return;
      }

      const persistedDiscount = normalizeMoneyForPersist(trimmedDiscount);
      const persistedAppliedAmount = normalizeMoneyForPersist(trimmedAppliedAmount);

      if (
        normalizedDiscount === latestPersistedRef.current.discount &&
        normalizedAppliedAmount === latestPersistedRef.current.appliedAmount
      ) {
        return;
      }

      try {
        await onUpdateAssignedInvoice(rowId, assignedLinkId, {
          invoice_discount: trimmedDiscount === '' ? null : persistedDiscount,
          applied_amount: trimmedAppliedAmount === '' ? null : persistedAppliedAmount,
          is_manual_open_assignment: Boolean(invoice?.is_manual_open_assignment),
        });

        // Only mark values as persisted after the server has accepted them.
        latestPersistedRef.current = {
          discount: normalizedDiscount,
          appliedAmount: normalizedAppliedAmount,
        };
        setDiscountValue(normalizedDiscount);
        setAppliedAmountValue(normalizedAppliedAmount);

        if (typeof onPreviewChange === 'function') {
          onPreviewChange({
            invoiceKey: previewInvoiceKey,
            previewInvoice: null,
          });
        }
      } catch (err) {
        // Keep Payment/Discount exactly as typed when the save fails.
        // The controller is responsible for surfacing the error.
      }
    },
    [
      assignedLinkId,
      editableAppliedAmount,
      editableDiscount,
      invoice,
      normalizeMoneyForCompare,
      normalizeMoneyForPersist,
      onPreviewChange,
      onUpdateAssignedInvoice,
      previewInvoiceKey,
      rowId,
    ]
  );

  React.useEffect(() => () => clearTimeout(idleTimerRef.current), []);

  // Validate/format/save only after 1s of inactivity, never on each keystroke.
  const handleFieldChange = React.useCallback(
    (field, nextInputValue) => {
      isEditingRef.current = true;
      const nextDiscount = field === 'discount' ? nextInputValue : discountValue;
      const nextApplied = field === 'discount' ? appliedAmountValue : nextInputValue;
      if (field === 'discount') setDiscountValue(nextInputValue);
      else setAppliedAmountValue(nextInputValue);

      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        isEditingRef.current = false;
        persistEditedValues(nextDiscount, nextApplied, { revertOnInvalid: false });
      }, 1000);
    },
    [appliedAmountValue, discountValue, persistEditedValues]
  );

  const handleFieldBlur = React.useCallback(
    (event, field) => {
      clearTimeout(idleTimerRef.current);
      isEditingRef.current = false;
      const nextInputValue = event?.target?.value ?? '';

      if (field === 'discount') {
        persistEditedValues(nextInputValue, appliedAmountValue, { revertOnInvalid: true });
        return;
      }

      persistEditedValues(discountValue, nextInputValue, { revertOnInvalid: true });
    },
    [appliedAmountValue, discountValue, persistEditedValues]
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 120px 110px',
        gap: 1,
        alignItems: 'start',
      }}
    >
      {/* Left side: clickable child row + checkbox + invoice summary */}
      <ListItemButton
        dense
        onClick={() => {
          if (hasActiveTextSelection()) {
            return;
          }

          onToggle(invoice);
        }}
        sx={{
          py: 0.125,
          px: 1.5,
          alignItems: 'flex-start',
          userSelect: 'text',
          WebkitUserSelect: 'text',
        }}
      >
        <Checkbox
          edge="start"
          checked={checked}
          tabIndex={-1}
          disableRipple
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggle(invoice)}
          sx={{ pt: 0.25, mr: 0.5 }}
        />
        <ListItemText
          primary={buildChildInvoicePrimary(rowPreviewInvoice || previewInvoice)}
          secondary={buildChildInvoiceSecondary(rowPreviewInvoice || previewInvoice)}
          primaryTypographyProps={{
            variant: 'body2',
            sx: {
              userSelect: 'text',
              WebkitUserSelect: 'text',
            },
          }}
          secondaryTypographyProps={{
            variant: 'caption',
            sx: {
              userSelect: 'text',
              WebkitUserSelect: 'text',
            },
          }}
        />
      </ListItemButton>

      {/* Right side (left input): editable payment amount for short-pay use cases */}
      <TextField
        id={`assigned-payment-${inputIdSuffix}`}
        name={`assigned-payment-${inputIdSuffix}`}
        size="small"
        label="Payment"
        value={appliedAmountValue}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => handleFieldChange('payment', e.target.value)}
        onBlur={(event) => handleFieldBlur(event, 'payment')}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
        sx={selectableTextFieldSx}
        inputProps={{ inputMode: 'decimal', style: { userSelect: 'text' } }}
      />

      {/* Right side: editable discount override for this assigned invoice */}
      <TextField
        id={`assigned-discount-${inputIdSuffix}`}
        name={`assigned-discount-${inputIdSuffix}`}
        size="small"
        label="Discount"
        value={discountValue}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => handleFieldChange('discount', e.target.value)}
        onBlur={(event) => handleFieldBlur(event, 'discount')}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
        sx={selectableTextFieldSx}
        inputProps={{ inputMode: 'decimal', style: { userSelect: 'text' } }}
      />
    </Box>
  );
}

AssignedInvoiceRow.propTypes = {
  invoice: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    manual_link_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    is_manual_open_assignment: PropTypes.bool,
    invoice_key: PropTypes.string,
    invoice_number: PropTypes.string,
    invoice_amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    invoice_discount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    invoice_discount_override: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    applied_amount_override: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    applied_amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    masid: PropTypes.string,
    masio_invoice_number: PropTypes.string,
    mas_customer_id: PropTypes.string,
    bill_to_name: PropTypes.string,
    payment_number: PropTypes.string,
    customer_name: PropTypes.string,
    score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    match_score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    confidence_score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    selected_source_kind: PropTypes.oneOf(['suggested', 'open']),
    source: PropTypes.string,
    match_reason: PropTypes.string,
  }).isRequired,
  rowId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  checked: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onUpdateAssignedInvoice: PropTypes.func,
  maxApplicableAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  previewInvoiceMap: PropTypes.objectOf(PropTypes.object),
  onPreviewChange: PropTypes.func,
};

AssignedInvoiceRow.defaultProps = {
  onUpdateAssignedInvoice: null,
  maxApplicableAmount: null,
  previewInvoiceMap: {},
  onPreviewChange: null,
};

// ============================================================================
// BLOCK BP.CMP.002.06 — Parent transaction row component
// ----------------------------------------------------------------------------
// PURPOSE
// - Renders one parent bank transaction row
// - Controls visual selected/checked styling
// - Expands to show assigned invoice child rows when appropriate
//
// EXPANSION RULE
// - Child rows open when:
//     1. transaction checkbox is checked, or
//     2. transaction is currently selected
// ============================================================================
function TransactionRow({
  row,
  selectedId,
  onSelect,
  checked,
  onToggleTransaction,
  assignedInvoices,
  checkedChildKeys,
  onToggleChildInvoice,
  onUpdateAssignedInvoice,
  previewInvoiceMap,
  onPreviewInvoiceChange,
}) {
  const liveAssignedInvoices = React.useMemo(
    () =>
      Array.isArray(assignedInvoices)
        ? assignedInvoices.map((invoice) => {
            const baseInvoiceKey =
              invoice?.invoice_key ||
              `manual:${resolveAssignedLinkId(invoice) || invoice?.source_invoice_key || 'unlinked'}`;

            const previewKey = `${normalizeTransactionKey(row.id)}::${baseInvoiceKey}`;
            const preview = previewInvoiceMap?.[previewKey];

            return preview
              ? {
                  ...invoice,
                  ...preview,
                }
              : invoice;
          })
        : [],
    [assignedInvoices, previewInvoiceMap, row.id]
  );
  const childrenOpen =
    checked || normalizeTransactionKey(selectedId) === normalizeTransactionKey(row.id);
  const isPlacementTarget = normalizeTransactionKey(selectedId) === normalizeTransactionKey(row.id);
  const isZeroBalance = isZeroBalanceTransaction(row, liveAssignedInvoices);
  const isNeedsInvoiceAction = isBalancedMissingInvoiceNumberTransaction(row, liveAssignedInvoices);
  const scoreBand = resolveBalancedTransactionScoreBand(row, liveAssignedInvoices);
  const isScoreBandLow = scoreBand === 'low';
  const isScoreBandMedium = scoreBand === 'medium';
  const isScoreBandStrong = scoreBand === 'strong';

  return (
    <Box>
      {/* Parent bank transaction row */}
      <ListItemButton
        dense
        selected={isPlacementTarget}
        onClick={() => {
          if (hasActiveTextSelection()) {
            return;
          }

          onSelect(row);
        }}
        data-balance-state={
          isZeroBalance ? 'balanced-ready' : isNeedsInvoiceAction ? 'needs-invoice' : 'open'
        }
        title={
          isNeedsInvoiceAction
            ? 'Action required: one or more assigned rows are missing invoice number.'
            : isScoreBandMedium
              ? 'Review suggested invoices before submitting.'
              : isScoreBandLow
                ? 'Low-confidence matched invoices; review before submitting.'
                : undefined
        }
        sx={{
          py: 0.25,
          px: 1.0,
          alignItems: 'flex-start',
          userSelect: 'text',
          WebkitUserSelect: 'text',
          borderLeft: '4px solid',
          borderLeftColor: isPlacementTarget
            ? 'primary.main'
            : isNeedsInvoiceAction
              ? 'warning.main'
              : checked
                ? 'warning.main'
                : isScoreBandLow
                  ? 'warning.dark'
                  : isScoreBandMedium
                    ? 'warning.main'
                    : isZeroBalance && isScoreBandStrong
                      ? 'success.light'
                      : 'transparent',
          bgcolor: isPlacementTarget
            ? 'action.selected'
            : isNeedsInvoiceAction
              ? 'rgba(255, 193, 7, 0.22)'
              : checked
                ? 'rgba(217, 122, 43, 0.12)'
                : isScoreBandLow
                  ? 'rgba(255, 152, 0, 0.22)'
                  : isScoreBandMedium
                    ? 'rgba(255, 193, 7, 0.22)'
                    : isZeroBalance && isScoreBandStrong
                      ? 'rgba(76, 175, 80, 0.16)'
                      : 'transparent',
        }}
      >
        <Checkbox
          edge="start"
          checked={checked}
          tabIndex={-1}
          disableRipple
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggleTransaction(row)}
          sx={{ pt: 0.25, mr: 0.5 }}
        />

        <ListItemText
          primary={buildTransactionPrimary(row, liveAssignedInvoices)}
          primaryTypographyProps={{
            variant: 'body2',
            sx: {
              userSelect: 'text',
              WebkitUserSelect: 'text',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            },
          }}
        />
      </ListItemButton>

      {/* Nested assigned invoices for this transaction */}
      {assignedInvoices.length > 0 && (
        <Collapse in={childrenOpen} timeout="auto" unmountOnExit>
          <Box sx={{ pl: 4 }}>
            <Stack spacing={0.5}>
              {assignedInvoices.map((invoice) => {
                /**
                 * childKey:
                 * - Uses invoice.invoice_key when available
                 * - Falls back to a composed stable-ish identifier
                 *
                 * scopedChildKey:
                 * - Prefixes transaction id so the same invoice-like key can exist
                 *   under multiple transactions without collisions
                 */
                const childKey =
                  invoice.invoice_key ||
                  `${invoice.source || 'selected'}:${invoice.invoice_number || ''}:${invoice.masid || ''}`;
                const scopedChildKey = `${normalizeTransactionKey(row.id)}::${childKey}`;
                const otherAppliedTotal = assignedInvoices.reduce(
                  (sum, candidate) =>
                    candidate === invoice ? sum : sum + resolveAppliedAmount(candidate),
                  0
                );
                const remainingForCurrentInvoice = Math.max(
                  toNumber(row?.amount) - otherAppliedTotal,
                  0
                );

                return (
                  <AssignedInvoiceRow
                    key={childKey}
                    invoice={invoice}
                    rowId={normalizeTransactionKey(row.id)}
                    checked={checkedChildKeys.includes(scopedChildKey)}
                    onToggle={(inv) => onToggleChildInvoice(inv, row)}
                    onUpdateAssignedInvoice={onUpdateAssignedInvoice}
                    maxApplicableAmount={remainingForCurrentInvoice}
                    previewInvoiceMap={previewInvoiceMap}
                    onPreviewChange={({ invoiceKey, previewInvoice }) => {
                      if (typeof onPreviewInvoiceChange === 'function') {
                        onPreviewInvoiceChange({ invoiceKey, previewInvoice });
                      }
                    }}
                  />
                );
              })}
            </Stack>
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

TransactionRow.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    balance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    description: PropTypes.string,
    full_description: PropTypes.string,
  }).isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSelect: PropTypes.func.isRequired,
  checked: PropTypes.bool.isRequired,
  onToggleTransaction: PropTypes.func.isRequired,
  assignedInvoices: PropTypes.arrayOf(PropTypes.object).isRequired,
  checkedChildKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleChildInvoice: PropTypes.func.isRequired,
  onUpdateAssignedInvoice: PropTypes.func,
  previewInvoiceMap: PropTypes.objectOf(PropTypes.object),
  onPreviewInvoiceChange: PropTypes.func,
};

TransactionRow.defaultProps = {
  selectedId: null,
  onUpdateAssignedInvoice: null,
  previewInvoiceMap: {},
  onPreviewInvoiceChange: null,
};

// ============================================================================
// BLOCK BP.CMP.002.07 — Main transaction tree component
// ----------------------------------------------------------------------------
// PURPOSE
// - Renders all date groups and transaction rows
// - Normalizes incoming props to safe defaults
// - Tracks local expand/collapse state for each date group
//
// DATA EXPECTATION
// - groups = [
//     {
//       date: '03-12-2026',
//       transactions: [...]
//     }
//   ]
// ============================================================================
export default function BankPostingTransactionTree({
  title,
  groups,
  selectedId,
  onSelect,
  checkedTransactionIds,
  onToggleTransaction,
  assignedInvoicesByTransactionId,
  checkedChildKeys,
  onToggleChildInvoice,
  onUpdateAssignedInvoice,
}) {
  // Defensive normalization so render logic does not fail on undefined props.
  const safeCheckedTransactionIds = Array.isArray(checkedTransactionIds)
    ? checkedTransactionIds
    : [];

  const safeAssignedInvoicesByTransactionId =
    assignedInvoicesByTransactionId && typeof assignedInvoicesByTransactionId === 'object'
      ? assignedInvoicesByTransactionId
      : {};

  const safeCheckedChildKeys = Array.isArray(checkedChildKeys) ? checkedChildKeys : [];
  const [previewInvoiceMap, setPreviewInvoiceMap] = React.useState({});

  /**
   * openMap tracks whether each date group is expanded.
   *
   * SHAPE
   * - {
   *     '03-12-2026': true,
   *     '03-13-2026': false
   *   }
   */
  const [openMap, setOpenMap] = React.useState(() =>
    Object.fromEntries(groups.map((row) => [row.date, false]))
  );

  /**
   * Keeps openMap in sync when new date groups appear.
   *
   * IMPORTANT
   * - Existing open/closed state is preserved
   * - Newly introduced dates default to collapsed (false)
   */
  React.useEffect(() => {
    setOpenMap((prev) => {
      const next = { ...prev };
      groups.forEach((group) => {
        if (!(group.date in next)) next[group.date] = false;
      });
      return next;
    });
  }, [groups]);

  React.useEffect(() => {
    const normalizedSelectedId = normalizeTransactionKey(selectedId);

    if (!normalizedSelectedId) {
      return;
    }

    const selectedGroup = (Array.isArray(groups) ? groups : []).find((group) =>
      (Array.isArray(group?.transactions) ? group.transactions : []).some(
        (row) => normalizeTransactionKey(row?.id) === normalizedSelectedId
      )
    );

    if (!selectedGroup?.date) {
      return;
    }

    setOpenMap((prev) => {
      if (prev[selectedGroup.date]) {
        return prev;
      }

      return {
        ...prev,
        [selectedGroup.date]: true,
      };
    });
  }, [groups, selectedId]);

  return (
    <Box
      sx={{ border: 1, borderColor: 'divider', minHeight: 360, p: 1, bgcolor: 'background.paper' }}
    >
      {/* Tree panel title */}
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        {title}
      </Typography>

      {/* Date-grouped transaction tree */}
      <List dense disablePadding sx={{ maxHeight: 560, overflowY: 'auto', overflowX: 'hidden' }}>
        {groups.map((group) => (
          <Box key={group.date}>
            {/* Date-group header row */}
            <ListItemButton
              dense
              onClick={() => setOpenMap((prev) => ({ ...prev, [group.date]: !prev[group.date] }))}
            >
              {openMap[group.date] ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
              <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 700 }}>
                {group.date}
              </Typography>
            </ListItemButton>

            {/* Transactions under this date group */}
            <Collapse in={openMap[group.date]}>
              <Stack spacing={0.25} sx={{ pl: 1 }}>
                {[...(Array.isArray(group.transactions) ? group.transactions : [])]
                  .sort((leftRow, rightRow) => {
                    const leftAssigned =
                      safeAssignedInvoicesByTransactionId[normalizeTransactionKey(leftRow.id)] ||
                      (Array.isArray(leftRow.selected_invoices) ? leftRow.selected_invoices : []);
                    const rightAssigned =
                      safeAssignedInvoicesByTransactionId[normalizeTransactionKey(rightRow.id)] ||
                      (Array.isArray(rightRow.selected_invoices) ? rightRow.selected_invoices : []);

                    const leftRank = resolveTransactionSortBand(leftRow, leftAssigned);
                    const rightRank = resolveTransactionSortBand(rightRow, rightAssigned);

                    if (leftRank !== rightRank) {
                      return leftRank - rightRank;
                    }

                    const amountDifference = toNumber(rightRow?.amount) - toNumber(leftRow?.amount);

                    if (Math.abs(amountDifference) > 0.0001) {
                      return amountDifference;
                    }

                    return normalizeTransactionKey(leftRow?.id).localeCompare(
                      normalizeTransactionKey(rightRow?.id)
                    );
                  })
                  .map((row) => (
                    <TransactionRow
                      key={normalizeTransactionKey(row.id)}
                      row={row}
                      selectedId={selectedId}
                      onSelect={onSelect}
                      checked={safeCheckedTransactionIds.includes(normalizeTransactionKey(row.id))}
                      onToggleTransaction={onToggleTransaction}
                      assignedInvoices={
                        safeAssignedInvoicesByTransactionId[normalizeTransactionKey(row.id)] ||
                        (Array.isArray(row.selected_invoices) ? row.selected_invoices : [])
                      }
                      checkedChildKeys={safeCheckedChildKeys}
                      onToggleChildInvoice={onToggleChildInvoice}
                      onUpdateAssignedInvoice={onUpdateAssignedInvoice}
                      previewInvoiceMap={previewInvoiceMap}
                      onPreviewInvoiceChange={({ invoiceKey, previewInvoice }) => {
                        if (!invoiceKey) return;
                        setPreviewInvoiceMap((prev) => {
                          if (!previewInvoice) {
                            if (!(invoiceKey in prev)) {
                              return prev;
                            }

                            const next = { ...prev };
                            delete next[invoiceKey];
                            return next;
                          }

                          const current = prev[invoiceKey];
                          const nextJson = JSON.stringify(previewInvoice);
                          const currentJson = JSON.stringify(current);

                          if (nextJson === currentJson) {
                            return prev;
                          }

                          return {
                            ...prev,
                            [invoiceKey]: previewInvoice,
                          };
                        });
                      }}
                    />
                  ))}
              </Stack>
            </Collapse>
          </Box>
        ))}
      </List>
    </Box>
  );
}

BankPostingTransactionTree.propTypes = {
  title: PropTypes.string.isRequired,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      transactions: PropTypes.array.isRequired,
    })
  ).isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSelect: PropTypes.func.isRequired,
  checkedTransactionIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ),
  onToggleTransaction: PropTypes.func.isRequired,
  assignedInvoicesByTransactionId: PropTypes.objectOf(PropTypes.array),
  checkedChildKeys: PropTypes.arrayOf(PropTypes.string),
  onToggleChildInvoice: PropTypes.func.isRequired,
  onUpdateAssignedInvoice: PropTypes.func,
};

BankPostingTransactionTree.defaultProps = {
  selectedId: null,
  checkedTransactionIds: [],
  assignedInvoicesByTransactionId: {},
  checkedChildKeys: [],
  onUpdateAssignedInvoice: null,
};
